import React from 'react';
import { Modal, Box, Typography, IconButton, Button, TextField, FormHelperText, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ServerURL } from '../serverURL';
import { useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const JobQuickCreateModal = ({
  open,
  onClose,
  jobUniqueCode,
  setJobUniqueCode,
  jobTitle,
  setJobTitle,
  jobUniqueCodeError = '',
  jobTitleError = '',
  charCounts = { jobUniqueCode: 0, jobTitle: 0 },
  handleInputChange = () => {},
  handleBlur = () => {},
  onJobCreated = () => {}, // <-- add callback prop
  useAgentAddJobPost = false,
  companyId = '',
  companyUserId = '',
  postCreateRedirectMode = 'company',
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleQuickCreateJob = async () => {
    setLoading(true);
    try {
      const adminAuthKey = localStorage.getItem('admin_auth_key');
      const authKey = localStorage.getItem('auth_key');
      const token = useAgentAddJobPost ? (adminAuthKey || authKey) : authKey;

      if (!token) {
        showSnackbar('セッションの有効期限が切れました。再度ログインをお願いします', 'error');
        setLoading(false);
        return;
      }

      if (useAgentAddJobPost && (!companyId || !companyUserId)) {
        showSnackbar('会社情報を選択してから求人作成してください', 'error');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('job_unique_code', jobUniqueCode);
      formData.append('job_title', jobTitle);
      if (useAgentAddJobPost) {
        formData.append('company_id', companyId);
        formData.append('company_user_id', companyUserId);
      }

      // Add other required fields as needed
      const endpoint = useAgentAddJobPost ? 'agent/add_job_post' : 'companies/job_post';
      const response = await fetch(`${ServerURL.PRODUCTION_HOST_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok) {
        showSnackbar(result.message || '求人が作成されました', 'success');
        onClose(); // Close modal first
        setTimeout(() => {
          onJobCreated(); // Refresh parent table after modal closes
        }, 300); // Small delay to ensure UI updates
        if (result.job_id) {
          if (postCreateRedirectMode === 'admin') {
            navigate(`/admin/company/company-job-posting-edit/${result.job_id}`, {
              state: {
                jobPostData: {
                  cjp_job_id: String(result.job_id),
                  cjp_company_id: String(companyId || ''),
                  cjp_company_user_id: String(companyUserId || ''),
                  cjp_job_code: jobUniqueCode || '',
                  cjp_job_title: jobTitle || '',
                },
                jobDetails: {
                  job_id: String(result.job_id),
                  company_id: String(companyId || ''),
                  company_user_id: String(companyUserId || ''),
                  job_code: jobUniqueCode || '',
                  job_title: jobTitle || '',
                },
                companyId: String(companyId || ''),
                companyUserId: String(companyUserId || ''),
              },
            });
          } else {
            navigate(`/company/jobregistration/jobedit/${result.job_id}?jobPostId=${btoa(result.job_id)}`);
          }
        }
      } else {
        showSnackbar(result.error?.message || '求人作成に失敗しました', 'error');
      }
    } catch (error) {
      showSnackbar('求人作成に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="jobquickcreate-modal-title"
        aria-describedby="jobquickcreate-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <IconButton
            size="small"
            color="inherit"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {/* <Typography id="jobquickcreate-modal-title" variant="h6" component="h2">
            新規求人登録
          </Typography>
          <Typography id="jobquickcreate-modal-description" sx={{ mt: 2, mb: 2 }}>
            求人IDと求人タイトルを入力してください。
          </Typography> */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography className="regCompanyHeader" sx={{ textAlign: 'left', lineHeight: 1 }}>
                求人No
              </Typography>
              <span
                className="mandatory"
                style={{
                  marginLeft: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '18px',
                  width: '27px',
                }}
              >
                必須
              </span>
            </Box>
            <TextField
              fullWidth
              name="job_unique_code"
              id="job_unique_code"
              autoComplete="job_unique_code"
              placeholder="求人Noを入力してください (GJN001)"
              autoFocus
              margin="normal"
              value={jobUniqueCode}
              onChange={e => setJobUniqueCode(e.target.value)}
              onBlur={() => handleBlur('jobUniqueCode')}
              error={jobUniqueCodeError !== ''}
              sx={{ backgroundColor: '#F6F6F6', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 6 }}
              InputProps={{
                endAdornment: (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '5px',
                      right: '10px',
                      color:
                        (charCounts.jobUniqueCode || 0) === 6
                          ? 'red'
                          : 'rgba(0, 0, 0, 0.6)',
                      fontSize: '12px',
                      fontFamily: '"Noto Sans JP", sans-serif',
                    }}
                  >
                    {`${charCounts.jobUniqueCode || 0}/6`}
                  </div>
                ),
                classes: { input: 'placeholder-style' },
                style: { alignItems: 'flex-start' },
              }}
            />
            <Typography variant="body2" sx={{ color: '#404040', fontFamily: '"Noto Sans JP", sans-serif', textAlign: 'left' }}>
              求人ごとに設定いただく管理番号です。<br />
              雇用形態＋連番など、管理しやすい形式でご入力ください。
            </Typography>
            
            {jobUniqueCodeError !== '' && (
              <FormHelperText sx={{ color: '#d32f2f' }} className="HelperText">
                {jobUniqueCodeError}
              </FormHelperText>
            )}
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography className="regCompanyHeader" sx={{ textAlign: 'left', lineHeight: 0 }}>
                求人タイトル
              </Typography>
              <span
                className="mandatory"
                style={{
                  marginLeft: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '18px',
                  width: '27px',
                }}
              >
                {' '}
                必須{' '}
              </span>
            </Box>
            <TextField
              fullWidth
              name="jobTitle"
              id="jobTitle"
              autoComplete="jobTitle"
              placeholder="社内管理向けのタイトルを入力してください(20字以内)"
              margin="normal"
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
              error={jobTitleError !== ''}
              onBlur={() => handleBlur('jobTitle')}
              sx={{ backgroundColor: '#F6F6F6', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 20 }}
              InputProps={{
                endAdornment: (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '5px',
                      right: '10px',
                      color:
                        (charCounts.jobTitle || 0) === 20
                          ? 'red'
                          : 'rgba(0, 0, 0, 0.6)',
                      fontSize: '12px',
                      fontFamily: '"Noto Sans JP", sans-serif',
                    }}
                  >
                    {`${charCounts.jobTitle || 0}/20`}
                  </div>
                ),
                classes: { input: 'placeholder-style' },
                style: { alignItems: 'flex-start' },
              }}
            />
            {jobTitleError !== '' && (
              <FormHelperText sx={{ color: '#d32f2f' }} className="HelperText">
                {jobTitleError}
              </FormHelperText>
            )}
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              className="BlueButton"
              variant="contained"
              onClick={onClose}
              sx={{ fontFamily: '"Noto Sans JP", sans-serif', minWidth: 150 }}
            >
              作成しない
            </Button>
            <Button
              className="yellowButton"
              variant="contained"
              onClick={handleQuickCreateJob}
              disabled={loading}
              sx={{ fontFamily: '"Noto Sans JP", sans-serif', minWidth: 150 }}
            >
              {loading ? (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={20} color="inherit" />
                  &nbsp;求人作成中...
                </span>
              ) : (
                '求人作成'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JobQuickCreateModal;
